import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Lightbulb, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, getStudentId } from "@/lib/api";
import { playCorrectChime, playStreakCelebration } from "@/lib/sounds";
import { whenAudioIdle } from "@/lib/audioLock";
import Navbar from "@/components/Navbar";
import { ConceptVisuals } from "@/components/VisualWidget";
import Celebration from "@/components/Celebration";
import VoicePromptBanner from "@/components/VoicePromptBanner";
import { useVoicePrompt } from "@/hooks/use-voice-prompt";
import { randomCorrectPhrase, randomIncorrectPhrase, withRandomIntro } from "@/lib/questionPhrases";

const AUTO_ADVANCE_DELAY_MS = 2800;

export default function Practice() {
  const { conceptId } = useParams();
  const navigate = useNavigate();
  const studentId = getStudentId();

  const [question, setQuestion] = useState(null);
  const [concept, setConcept] = useState(null);
  const [answer, setAnswer] = useState("");
  const [hint, setHint] = useState(null);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [startTime, setStartTime] = useState(Date.now());
  const [count, setCount] = useState(1);
  const [streak, setStreak] = useState(0);
  const [celebrationTrigger, setCelebrationTrigger] = useState(0);
  const [celebrationMessage, setCelebrationMessage] = useState("");

  const { blocked: voiceBlocked, retry: retryVoice } = useVoicePrompt(
    "Let's see how much you know in this section.",
    `practice-intro-${conceptId}`
  );
  const questionVoiceText = useMemo(() => (question ? withRandomIntro(question.text) : ""), [question?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const { blocked: questionVoiceBlocked, retry: retryQuestionVoice } = useVoicePrompt(
    questionVoiceText,
    question ? `practice-q-${question.id}` : null,
    { enabled: !!question }
  );
  const feedbackVoiceText = useMemo(
    () => (feedback ? (feedback.correct ? randomCorrectPhrase() : randomIncorrectPhrase()) : ""),
    [feedback] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const { blocked: feedbackVoiceBlocked, retry: retryFeedbackVoice } = useVoicePrompt(
    feedbackVoiceText,
    feedback && question ? `practice-feedback-${question.id}` : null,
    { enabled: !!feedback }
  );

  const loadQuestion = async () => {
    setFeedback(null);
    setHint(null);
    setHintsUsed(0);
    setAnswer("");
    setStartTime(Date.now());
    const { data } = await api.get("/practice/next", { params: { student_id: studentId, concept_id: conceptId } });
    setQuestion(data);
  };

  useEffect(() => {
    loadQuestion();
    api.get(`/curriculum/concepts/${conceptId}`).then(({ data }) => setConcept(data));
  }, [conceptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const getHint = async () => {
    const { data } = await api.post(`/practice/hint?question_id=${question.id}`);
    setHint(data.hint);
    setHintsUsed((h) => h + 1);
  };

  const submit = async () => {
    if (!answer.trim()) {
      toast.error("Please enter an answer first.");
      return;
    }
    const time_taken_sec = Math.round((Date.now() - startTime) / 1000);
    const { data } = await api.post("/practice/submit", {
      student_id: studentId, concept_id: conceptId, question_id: question.id, answer, hints_used: hintsUsed, time_taken_sec,
    });
    setFeedback(data);
    if (data.correct) {
      playCorrectChime();
      const newStreak = streak + 1;
      setStreak(newStreak);
      if (newStreak % 3 === 0) {
        setCelebrationMessage(`${newStreak} in a row! 🔥`);
        setCelebrationTrigger((t) => t + 1);
        playStreakCelebration();
      }
    } else {
      setStreak(0);
    }
  };

  const goNext = () => { setCount((c) => c + 1); loadQuestion(); };

  useEffect(() => {
    if (!feedback) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      await whenAudioIdle();
      if (!cancelled) goNext();
    }, AUTO_ADVANCE_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback]);

  if (!question) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading question...</div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar student={null} />
      <Celebration trigger={celebrationTrigger} message={celebrationMessage} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/")} data-testid="practice-back-button" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft size={15} /> Back to Home
        </button>
        <VoicePromptBanner
          blocked={voiceBlocked || questionVoiceBlocked || feedbackVoiceBlocked}
          onRetry={() => { retryVoice(); retryQuestionVoice(); retryFeedbackVoice(); }}
          testId="practice-voice-unlock-banner"
        />
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-heading font-bold">Practice · Question {count}</h1>
          <span data-testid="practice-level-badge" className="text-xs font-bold bg-primary/10 text-primary px-3 py-1 rounded-full">Level {question.level}/5</span>
        </div>

        <ConceptVisuals concept={concept} />

        <div className="bg-card border border-border rounded-2xl p-6" data-testid="practice-question-card">
          <p className="text-lg font-semibold mb-5" data-testid="practice-question-text">{question.text}</p>

          {question.type === "mcq" && (
            <div className="space-y-2">
              {question.options?.map((opt) => (
                <button key={opt} disabled={!!feedback} data-testid={`practice-option-${opt.replace(/\s+/g, '-')}`}
                  onClick={() => setAnswer(opt)}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${
                    answer === opt ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                  }`}>
                  {opt}
                </button>
              ))}
            </div>
          )}
          {question.type !== "mcq" && (
            <Input data-testid="practice-answer-input" value={answer} onChange={(e) => setAnswer(e.target.value)} disabled={!!feedback}
              placeholder="Type your answer..." className="text-base" />
          )}

          {hint && <p className="mt-3 text-sm text-secondary bg-secondary/10 rounded-lg px-3 py-2" data-testid="practice-hint-text"><Lightbulb size={13} className="inline mr-1" />{hint}</p>}

          {!feedback ? (
            <div className="flex flex-wrap items-center gap-3 mt-5">
              <Button variant="outline" size="sm" data-testid="practice-hint-button" onClick={getHint}><Lightbulb size={14} className="mr-1" /> Get Hint</Button>
              <Button data-testid="practice-submit-button" onClick={submit} className="ml-auto rounded-xl px-6">Submit Answer</Button>
            </div>
          ) : (
            <div className="mt-5 animate-float-in" data-testid="practice-feedback">
              <div className={`flex items-center gap-2 font-semibold mb-2 ${feedback.correct ? "text-success" : "text-destructive"}`}>
                {feedback.correct ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
                {feedback.correct ? "Correct!" : "Not quite"}
              </div>
              <p className="text-sm text-muted-foreground mb-3">{feedback.feedback}</p>
              <p className="text-xs text-muted-foreground mb-3">Mastery on this concept: {feedback.mastery_score}%</p>
              {feedback.suggest_microlesson && (
                <button onClick={() => navigate(`/tutor/learn/${conceptId}`)} data-testid="practice-microlesson-cta" className="text-sm text-primary font-semibold underline mb-3 block">
                  Struggling? Get a quick re-explanation →
                </button>
              )}
              <Button data-testid="practice-next-button" onClick={goNext} className="rounded-xl px-6">Continue now →</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
